import { type SceneDocument } from '@/domain/model';
import { encodeToJson, type LoadResult, loadDocumentFromJson } from '@/serialization';

/**
 * Import and export as a file.
 *
 * The browser has no way to write a file without a user gesture, so this is a download
 * link and a file input — the same mechanism every web editor uses. The File System
 * Access API would allow true save-in-place, but it is Chromium-only; this path works
 * everywhere and the repository already covers the persistent case.
 */

export const FILE_EXTENSION = '.edusketch.json';
export const MIME_TYPE = 'application/json';

/** Turns a title into something safe for a filename. */
export function suggestFilename(title: string): string {
  const cleaned = title
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .toLowerCase();
  return `${cleaned === '' ? 'untitled' : cleaned}${FILE_EXTENSION}`;
}

export interface DownloadHost {
  createObjectURL(blob: Blob): string;
  revokeObjectURL(url: string): void;
  /** Triggers the download. Separated so tests can observe it without a real DOM. */
  triggerDownload(url: string, filename: string): void;
}

export function browserDownloadHost(document: Document): DownloadHost {
  return {
    createObjectURL: (blob) => URL.createObjectURL(blob),
    revokeObjectURL: (url) => {
      URL.revokeObjectURL(url);
    },
    triggerDownload: (url, filename) => {
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    },
  };
}

export interface ExportOptions {
  /** Unknown fields from a newer writer, so an exported file loses nothing. */
  readonly preserve?: Readonly<Record<string, unknown>> | undefined;
}

export function exportDocument(
  host: DownloadHost,
  document: SceneDocument,
  options: ExportOptions = {}
): void {
  const text = encodeToJson(document, { preserve: options.preserve });
  const url = host.createObjectURL(new Blob([text], { type: MIME_TYPE }));
  try {
    host.triggerDownload(url, suggestFilename(document.title));
  } finally {
    // Revoked in `finally` so a failed download does not leak the object URL, which
    // pins the whole serialised document in memory until the tab closes.
    host.revokeObjectURL(url);
  }
}

/** Reads and validates a file the user picked. */
export async function importDocument(file: Blob): Promise<LoadResult> {
  const text = await file.text();
  return loadDocumentFromJson(text);
}
